import { getToken } from "../../utils/auth";

const API_BASE = process.env.REACT_APP_API_CRM_POSTVENTA || "/api/crm-postventa";
const LEGACY_CUPONES_LISTA = process.env.REACT_APP_API_LISTA_CRUDO || "/api/datosinicio/lista";
const LEGACY_LEGAJO_CREAR = process.env.REACT_APP_API_LEGAJO_CREAR || "/api/legajo/crearlegajo";
const LEGACY_LEGAJO_EDITAR =
  process.env.REACT_APP_API_LEGAJO_EDITAR_LEGAJO ||
  process.env.REACT_APP_API_LEGAJO_EDITAR ||
  "/api/legajo/editarlegajo";

const buildClienteKey = (clienteOrHeader = {}) => {
  if (typeof clienteOrHeader === "string") {
    return clienteOrHeader;
  }

  const clienteKey = clienteOrHeader?.clienteKey || clienteOrHeader?.ClienteKey;
  if (clienteKey) {
    return clienteKey;
  }

  const cuit = String(clienteOrHeader?.cuit || clienteOrHeader?.Cuit || "").replace(/\D/g, "");
  const comercio = String(
    clienteOrHeader?.nDeComercio ||
      clienteOrHeader?.NDeComercio ||
      clienteOrHeader?.comercio ||
      clienteOrHeader?.NumeroComercio ||
      ""
  ).replace(/\D/g, "");

  if (!cuit) {
    const clienteId = Number(clienteOrHeader?.clienteId || clienteOrHeader?.ClienteId || 0);
    return clienteId > 0 ? `alta-${clienteId}` : "alta-0";
  }

  return comercio ? `cuit-${cuit}__comercio-${comercio}` : `cuit-${cuit}`;
};

async function requestJson(path, { method = "POST", payload = null, query = null, includeTokenInBody = true } = {}) {
  const token = getToken();
  if (!token) {
    throw new Error("No hay sesion activa.");
  }

  const queryParams = new URLSearchParams();
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.set(key, String(value));
      }
    });
  }

  const headers = {
    "X-CRM-Token": token,
  };

  const options = {
    method,
    headers,
  };

  if (payload !== null) {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(
      includeTokenInBody
        ? {
            Token: token,
            ...payload,
          }
        : payload
    );
  }

  const url = `${API_BASE}${path}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  const response = await fetch(url, options);

  const raw = await response.text();
  let data = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    data = raw;
  }

  if (!response.ok) {
    const message =
      data?.mensaje ||
      data?.message ||
      (typeof data === "string" && data) ||
      `Error ${response.status}`;
    throw new Error(message);
  }

  return data;
}

async function postJson(path, payload = {}, includeTokenInBody = true) {
  return requestJson(path, {
    method: "POST",
    payload,
    includeTokenInBody,
  });
}

async function postAbsoluteJson(url, payload = {}) {
  const token = getToken();
  if (!token) {
    throw new Error("No hay sesion activa.");
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      Token: token,
      ...payload,
    }),
  });

  const raw = await response.text();
  let data = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    data = raw;
  }

  if (!response.ok) {
    const message =
      data?.mensaje ||
      data?.message ||
      (typeof data === "string" && data) ||
      `Error ${response.status}`;
    throw new Error(message);
  }

  return data;
}

async function getJson(path, query = {}) {
  return requestJson(path, {
    method: "GET",
    query,
  });
}

async function deleteJson(path, query = {}) {
  return requestJson(path, {
    method: "DELETE",
    query,
  });
}

async function requestDirectJson(url, { method = "POST", payload = null, query = null, includeTokenInBody = true } = {}) {
  const token = getToken();
  if (!token) {
    throw new Error("No hay sesion activa.");
  }

  const queryParams = new URLSearchParams();
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.set(key, String(value));
      }
    });
  }

  const headers = {
    "X-CRM-Token": token,
  };

  const options = {
    method,
    headers,
  };

  if (payload !== null) {
    headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(
      includeTokenInBody
        ? {
            Token: token,
            ...payload,
          }
        : payload
    );
  }

  const finalUrl = `${url}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  const response = await fetch(finalUrl, options);
  const raw = await response.text();

  let data = null;
  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    data = raw;
  }

  if (!response.ok) {
    const message =
      data?.mensaje ||
      data?.message ||
      (typeof data === "string" && data) ||
      `Error ${response.status}`;
    throw new Error(message);
  }

  return data;
}

const formatLegacyCuponesMonth = (value) => {
  if (!value) return "";
  const [year, month] = String(value).split("-");
  if (!year || !month) {
    return String(value);
  }

  return `${month}/${String(year).slice(-2)}`;
};

export const crmPostventaApi = {
  buildClienteKey,
  buscarClientes(search) {
    return postJson("/clientes", { Search: search || "" });
  },
  obtenerFicha(clienteId) {
    return postJson("/clientes/ficha", { ClienteId: clienteId });
  },
  actualizarFicha(clienteId, payload) {
    return postJson("/clientes/ficha/actualizar", {
      ClienteId: clienteId,
      ...payload,
    });
  },
  obtenerFacturacion(clienteId, desde, hasta) {
    return postJson("/clientes/facturacion", {
      ClienteId: clienteId,
      Desde: desde,
      Hasta: hasta,
    });
  },
  obtenerOrdenesPago(clienteId, fechaPago, search) {
    return postJson("/clientes/ordenes-pago", {
      ClienteId: clienteId,
      FechaPago: fechaPago,
      Search: search || "",
    });
  },
  obtenerOrdenPagoPdf(clienteId, fechaPago, documentoKey) {
    return postJson("/clientes/ordenes-pago/pdf", {
      ClienteId: clienteId,
      FechaPago: fechaPago,
      DocumentoKey: documentoKey || "",
    });
  },
  obtenerCuponesLegacy(fechaReferencia) {
    return postAbsoluteJson(LEGACY_CUPONES_LISTA, {
      Fecha: formatLegacyCuponesMonth(fechaReferencia),
    });
  },
  obtenerCupones(clienteId, desde, hasta, search) {
    return postJson("/clientes/cupones", {
      ClienteId: clienteId,
      Desde: desde,
      Hasta: hasta,
      Search: search || "",
    });
  },
  obtenerAlertas(filtros = {}) {
    return postJson("/alertas", {
      FechaReferencia: filtros.fecha || "",
      Estado: filtros.estado || "",
      Prioridad: filtros.prioridad || "",
      Tipo: filtros.tipo || "",
      ProvinciaFiltro: filtros.provincia || "",
      Responsable: filtros.responsable || "",
      Search: filtros.search || "",
    });
  },
  obtenerInactivos(filtros = {}) {
    return postJson("/inactivos", {
      FechaReferencia: filtros.fecha || "",
      ProvinciaFiltro: filtros.provincia || "",
      Responsable: filtros.responsable || "",
      DiasSinOperarRango: filtros.diasSinOperar || "",
      Search: filtros.search || "",
    });
  },
  generarGestionesAutomaticas(fecha) {
    return requestJson("/gestiones/generar-automaticas", {
      method: "POST",
      query: { fecha },
      includeTokenInBody: false,
    });
  },
  obtenerGestiones(clienteId) {
    return postJson("/clientes/gestiones", { ClienteId: clienteId });
  },
  crearGestion(
    clienteId,
    reglaId,
    estadoGestion,
    observacion,
    fechaGestion,
    fechaProximoContacto,
    motivoCodigo,
    canalContacto,
    resultadoLlamada,
    evidenciaContacto,
    resultadoZocoCheck,
    derivacionZocoCheck,
    campaniaId,
    idZocoCheckFlujo = null,
    seguimientoId = null,
    estadoContacto = null,
    resultadoContacto = null
  ) {
    return postJson("/clientes/gestiones/crear", {
      ClienteId: clienteId,
      ReglaId: reglaId,
      EstadoGestion: estadoGestion,
      Observacion: observacion,
      FechaGestion: fechaGestion,
      FechaProximoContacto: fechaProximoContacto,
      MotivoCodigo: motivoCodigo,
      CanalContacto: canalContacto,
      ResultadoLlamada: resultadoLlamada,
      EvidenciaContacto: evidenciaContacto,
      ResultadoZocoCheck: resultadoZocoCheck,
      DerivacionZocoCheck: derivacionZocoCheck,
      CampaniaId: campaniaId,
      IdZocoCheckFlujo: idZocoCheckFlujo,
      SeguimientoId: seguimientoId,
      EstadoContacto: estadoContacto,
      ResultadoContacto: resultadoContacto,
    });
  },
  actualizarGestion(payload) {
    return postJson("/clientes/gestiones/actualizar", payload);
  },
  finalizarGestion(payload) {
    return postJson("/clientes/gestiones/finalizar", payload);
  },
  bootstrapZocoCheck(fecha) {
    return requestJson("/zoco-check/bootstrap", {
      method: "POST",
      query: { fecha },
      includeTokenInBody: false,
    });
  },
  obtenerZocoCheckCampanias() {
    return getJson("/zoco-check/campanias");
  },
  obtenerZocoCheckOperativos(fecha) {
    return getJson("/zoco-check/operativos", {
      fecha: fecha || "",
    });
  },
  obtenerZocoCheckOperativo(flujoId) {
    return getJson(`/zoco-check/${flujoId}`);
  },
  ejecutarZocoCheckAccion(flujoId, payload) {
    return postJson(`/zoco-check/${flujoId}/acciones`, payload);
  },
  guardarZocoCheckCampania(payload) {
    return postJson("/zoco-check/campanias", payload);
  },
  cambiarEstadoZocoCheckCampania(id, activa) {
    return postJson("/zoco-check/campanias/estado", {
      Id: id,
      Activa: activa,
    });
  },
  eliminarZocoCheckCampania(id) {
    return deleteJson(`/zoco-check/campanias/${id}`);
  },
  obtenerRollos(clienteKey, clienteId) {
    return getJson(`/clientes/${encodeURIComponent(buildClienteKey(clienteKey))}/rollos`, { clienteId });
  },
  registrarRollo(cliente, fechaEntrega, cantidad, observacion) {
    const clienteId = Number(cliente?.clienteId || cliente?.ClienteId || cliente || 0);
    const clienteKey = buildClienteKey(cliente);

    return postJson(`/clientes/${encodeURIComponent(clienteKey)}/rollos`, {
      ClienteId: clienteId,
      ClienteKey: clienteKey,
      FechaEntrega: fechaEntrega,
      Cantidad: cantidad,
      Observacion: observacion,
    });
  },
  obtenerSeguimiento(clienteId, desde, hasta) {
    return postJson("/clientes/seguimiento", {
      ClienteId: clienteId,
      Desde: desde,
      Hasta: hasta,
    });
  },
  buscarUsuarioPorCuit(cuit) {
    return requestDirectJson("/api/usuarios/buscarusuario", {
      method: "POST",
      payload: {
        Usuario: cuit,
      },
    });
  },
  crearUsuarioAliado(payload) {
    return requestDirectJson("/api/liquidaciones/crearusuario", {
      method: "POST",
      payload,
    });
  },
  editarUsuarioAliado(payload) {
    return requestDirectJson("/api/liquidaciones/editarusuario", {
      method: "POST",
      payload,
    });
  },
  obtenerLegajos() {
    return requestDirectJson("/api/legajo/listacompleto", {
      method: "GET",
      includeTokenInBody: false,
    });
  },
  obtenerLegajosPorUsuario(iduser) {
    return requestDirectJson("/api/legajo/obtenerlegajoporiduser", {
      method: "POST",
      payload: {
        Iduser: iduser,
      },
    });
  },
  crearLegajo(payload) {
    return requestDirectJson(LEGACY_LEGAJO_CREAR, {
      method: "POST",
      payload,
    });
  },
  editarLegajo(payload) {
    return requestDirectJson(LEGACY_LEGAJO_EDITAR, {
      method: "POST",
      payload,
    });
  },
  listarDocumentosLegajo(idLegajo) {
    return requestDirectJson("/api/documentolegajo/listar-metadata", {
      method: "POST",
      payload: {
        IdLegajo: idLegajo,
      },
    });
  },
  subirDocumentoLegajo(payload) {
    return requestDirectJson("/api/documentolegajo/crear", {
      method: "POST",
      payload,
    });
  },
  eliminarDocumentoLegajo(idDocumento) {
    return requestDirectJson("/api/documentolegajo/eliminar", {
      method: "POST",
      payload: {
        IdDocumento: idDocumento,
      },
    });
  },
  crearFormularioAlta(payload) {
    return requestDirectJson("/api/formulario/formularioaltacrear", {
      method: "POST",
      payload,
      includeTokenInBody: false,
    });
  },
  crearAltaCrm(payload) {
    return requestDirectJson("/api/altas/agregaralta", {
      method: "POST",
      payload,
      includeTokenInBody: false,
    });
  },
  crearTerminalBaseAlta(payload) {
    return requestDirectJson("/api/altas/crear-terminal-base", {
      method: "POST",
      payload,
    });
  },
  ejecutarBajaTerminal(payload) {
    return requestDirectJson("/api/abm/baja", {
      method: "POST",
      payload,
    });
  },
};
